/**
 * 未ログインでも開ける画面のパス（Issue #8734 で middleware から切り出した）。
 *
 * middleware と、失効したセッションをサインインへ送る `AuthProvider` の両方が使う。
 * middleware 側の `next/server` などに依存させず、クライアントからも読み込めるようにしている。
 */

// 注意: `/api` および `/api/auth` は middleware `config.matcher` 側で
// 既に除外しているのでここに含めない (含めても dead code、含めると `/share`
// 同様 `/api-public-foo` のような名前衝突リスクが将来増える)。
export const PUBLIC_PREFIXES = [
  "/auth/signin",
  "/auth/signup",
  "/auth/invitation-redirect",
  "/auth/signout",
  "/auth/error",
  "/auth/post-signin",
  "/auth/verify-email",
  "/auth/forgot-password",
  "/auth/reset-password",
  // ADR-0043: 認証アプリを失くした利用者の復旧（未ログインで使う）
  "/auth/mfa-recovery",
  // ADR-0029 Phase 2: ブレークグラス（SSO の強制で通常のログインができないときの緊急用）。
  // 通常のセッション無しで開けないと意味が無い（#8737）。画面は入力欄だけで、API
  // （`/api/break-glass/*`、middleware の対象外）は専用 Pool のパスワード + TOTP と
  // Bearer トークンで守られているので、画面を公開しても認可の境界は変わらない
  "/auth/break-glass",
  "/mermaid/demo",
  "/.well-known",
  "/privacy",
  "/terms",
  "/share",
  "/_next",
  "/images",
  "/favicon.ico",
] as const;

// 公開アンケート回答画面は `/survey/{publicToken}` のみ。将来 `/survey/list` のような
// 認証必須パスを追加するときに事故らないよう、token セグメントの形だけを許す。
// publicToken は randomBytes(16).toString('base64url') = 22文字の base64url 文字列。
const PUBLIC_SURVEY_TOKEN_RE = /^\/survey\/[A-Za-z0-9_-]+$/;
const isPublicSurveyPath = (pathname: string): boolean => PUBLIC_SURVEY_TOKEN_RE.test(pathname);

/**
 * `pathname` が `prefix` で始まり、かつ次がパス境界 (`/` or 終端) であるかを判定する。
 * 単純な `startsWith` だと `/share-private` も `/share` prefix にヒットしてしまう
 * (false-positive)。`/share` 完全一致 OR `/share/...` のみを true にする。
 */
export function pathnameStartsWithBoundary(pathname: string, prefix: string): boolean {
  if (pathname === prefix) return true;
  return pathname.startsWith(prefix + "/");
}

export const isPublicPath = (pathname: string): boolean =>
  pathname === '/github/approve' ||
  isPublicSurveyPath(pathname) ||
  PUBLIC_PREFIXES.some((prefix) => pathnameStartsWithBoundary(pathname, prefix));

/**
 * ここが開かれているのに未ログインなら、サインインへ送るべき画面か（Issue #8734）。
 *
 * 判定は middleware と同じ公開パスの一覧だけで行う。`/auth/*` でも、一覧に無い画面
 * （`/auth/mfa-required` など）は middleware が Cookie を求める保護された画面なので送る。
 * middleware が Cookie の無い利用者を先に送っているので、画面まで来て未ログインになるのは、
 * Cookie はあるがセッションが失効している（`/api/auth/session` が `{}`）場合。
 */
export const isSignInRequiredPath = (pathname: string): boolean => !isPublicPath(pathname);
